import { useState, useRef } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Quiz Activity Component
 * Clean UI design with letter-based options, circular timer, and questions list sidebar
 */

const QuizActivity = ({
  activity,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  onBackToList,
  showResults = false,
  quizResults = null,
  isActivityEnded = false,
}) => {
  // Quiz questions. Planned quizzes store config.questions[]; quizzes added from
  // the live "Add Activity" form were saved as a single flat config.question +
  // config.options (the backend now wraps these, but older ones exist). Read both.
  // Never fall back to placeholder content: a hardcoded demo quiz used to live
  // here, and participants saw its questions instead of the trainer's.
  const toOptionText = (o) => (typeof o === 'string' ? o : (o?.text ?? ''));
  const quizQuestions = (() => {
    const list = activity.config?.questions || activity.questions;
    if (Array.isArray(list) && list.length > 0) return list;
    const question = activity.config?.question || activity.question;
    const options = activity.config?.options || activity.options;
    if (question && Array.isArray(options) && options.length > 0) {
      return [{
        id: 1,
        question,
        options: options.map(toOptionText),
        correctAnswer: activity.config?.correctAnswer ?? 0,
      }];
    }
    return [];
  })();

  const [quizAnswers, setQuizAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const totalQuestions = quizQuestions.length;

  // Wall-clock start time for the participant's quiz attempt. Used to
  // compute completionTime on submit, independent of whether the trainer
  // enabled a countdown timer. Without this, completionTime was derived
  // from `maxTime - timeRemaining` which is always 0 when no timer is set
  // (maxTime=0) and stays inaccurate even when a timer is set since the
  // parent's `timeRemaining` keeps ticking past the submit moment.
  const startedAtRef = useRef(Date.now());

  // Letter labels for options
  const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress - use actual timeLimit from activity
  const maxTime = activity?.timerEnabled === true
    ? (activity.timeLimit || activity.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  const handleQuestionClick = (questionNum) => {
    setCurrentQuestion(questionNum);
  };

  const handleNextQuestion = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    let correctCount = 0;
    let incorrectCount = 0;
    const questionDetails = [];

    quizQuestions.forEach((q, index) => {
      const userAnswer = quizAnswers[`q${index + 1}`];
      // Convert both to numbers for comparison (handles string/number type mismatch)
      const userAnswerNum = Number(userAnswer);
      const correctAnswerNum = Number(q.correctAnswer);
      const isCorrect = !isNaN(userAnswerNum) && !isNaN(correctAnswerNum) && userAnswerNum === correctAnswerNum;

      if (isCorrect) {
        correctCount++;
      } else {
        incorrectCount++;
      }

      // Store question details for reports
      questionDetails.push({
        questionNumber: index + 1,
        question: q.question,
        options: q.options,
        correctAnswer: q.correctAnswer,
        userAnswer: userAnswer !== undefined ? userAnswer : null,
        isCorrect,
      });
    });

    const totalPoints = totalQuestions * 10;
    const score = correctCount * 10;
    const percentage = Math.round((score / totalPoints) * 100);

    // Wall-clock elapsed seconds since the participant landed on the quiz.
    // Works whether or not the trainer enabled a countdown timer.
    const elapsedSeconds = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000));

    const results = {
      score: score,
      totalPoints: totalPoints,
      percentage: percentage,
      passed: percentage >= 70,
      correct: correctCount,
      incorrect: incorrectCount,
      completionTime: formatTime(elapsedSeconds),
    };
    onSubmit({ answers: quizAnswers, results: results, questionDetails: questionDetails });
  };

  const currentQ = quizQuestions[currentQuestion - 1];
  const answeredCount = Object.keys(quizAnswers).length;

  // Activity Ended but Not Responded View
  if (isActivityEnded && !showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">QUIZ</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this quiz activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Already Responded View (when showResults is true but no detailed results available)
  if (showResults && !quizResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isActivityEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">QUIZ</h2>
          </div>
        </div>

        {/* Already Responded Message */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 sm:p-8 text-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-green-800 mb-2">Already Responded!</h2>
            <p className="text-sm sm:text-base text-green-700 mb-4">You have already submitted your response for this quiz.</p>
            {isActivityEnded ? (
              <p className="text-xs sm:text-sm text-green-600">This activity has ended. Your answers have been recorded.</p>
            ) : (
              <p className="text-xs sm:text-sm text-green-600">Your answers have been recorded. Results will be available when the trainer shares them.</p>
            )}
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults && quizResults) {
    // Get answers from quizResults if available, otherwise use local state
    const answersToUse = quizResults.answers || quizAnswers;

    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-green-700">Live</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">QUIZ</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Score Stats */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="text-center p-2 sm:p-4 bg-green-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-green-600">{quizResults.correct}</div>
                <p className="text-xs sm:text-sm text-green-700">Correct</p>
              </div>
              <div className="text-center p-2 sm:p-4 bg-red-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-red-600">{quizResults.incorrect}</div>
                <p className="text-xs sm:text-sm text-red-700">Incorrect</p>
              </div>
              <div className="text-center p-2 sm:p-4 bg-neutral-50 rounded-lg">
                <div className="text-xl sm:text-2xl font-bold text-neutral-600">{quizResults.completionTime}</div>
                <p className="text-xs sm:text-sm text-neutral-700">Time</p>
              </div>
            </div>
          </div>

          {/* Question Review */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-neutral-900">Question Review</h3>
            {quizQuestions.map((q, index) => {
              const userAnswer = answersToUse[`q${index + 1}`];
              const hasAnswer = userAnswer !== undefined && userAnswer !== null;
              // Convert both to numbers for comparison (handles string/number type mismatch)
              const isCorrect = hasAnswer && Number(userAnswer) === Number(q.correctAnswer);

              return (
                <div key={index} className={`p-4 rounded-xl border-2 ${isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                      {isCorrect ? (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-neutral-900 mb-2">Q{index + 1}. {q.question}</p>
                      <p className="text-sm">
                        <span className="text-neutral-600">Your answer: </span>
                        {hasAnswer ? (
                          <span className={isCorrect ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                            {optionLetters[userAnswer]}. {q.options[userAnswer]}
                          </span>
                        ) : (
                          <span className="text-neutral-500 font-medium italic">Not answered</span>
                        )}
                      </p>
                      {!isCorrect && (
                        <p className="text-sm mt-1">
                          <span className="text-neutral-600">Correct answer: </span>
                          <span className="text-green-700 font-medium">
                            {optionLetters[q.correctAnswer]}. {q.options[q.correctAnswer]}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // No questions — show a clear message instead of crashing on currentQ.
  if (totalQuestions === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">This quiz has no questions</h2>
          <p className="text-sm text-neutral-500 mb-6">Please let your trainer know.</p>
          {onBackToList && (
            <button onClick={onBackToList} className="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-50">
              Back to activities
            </button>
          )}
        </div>
      </div>
    );
  }

  // Input View - Clean Design
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">QUIZ</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Timer Section - Mobile (shown at top on mobile) */}
          <div className="lg:hidden order-first">
            {timerEnabled && (
            <div className="flex flex-row items-center gap-4 mb-4 p-4 bg-neutral-50 rounded-xl">
              <div className="relative w-16 h-16">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#ff8a00"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-base font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                </div>
              </div>
              <div className="flex-1">
                <span className="text-sm text-neutral-500">Time Remaining</span>
                <div className="text-xs text-neutral-400 mt-1">{answeredCount}/{totalQuestions} answered</div>
              </div>
            </div>
            )}
            {/* Mobile Question Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {quizQuestions.map((q, index) => {
                const questionNum = index + 1;
                const isAnswered = quizAnswers[`q${questionNum}`] !== undefined;
                const isCurrent = currentQuestion === questionNum;
                return (
                  <button
                    key={index}
                    onClick={() => handleQuestionClick(questionNum)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      isAnswered
                        ? 'bg-green-500 text-white'
                        : isCurrent
                          ? 'bg-primary-500 text-white'
                          : 'bg-neutral-200 text-neutral-600'
                    }`}
                  >
                    {isAnswered ? '✓' : questionNum}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Left Side - Question & Options */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Question Number Badge */}
            <div className="flex items-center gap-3 mb-3 lg:mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-semibold rounded-full">
                Question {currentQuestion} of {totalQuestions}
              </span>
            </div>

            {/* Question Text */}
            <h1 className="text-lg lg:text-2xl font-bold text-neutral-900 mb-6 lg:mb-8 leading-tight">
              {currentQ.question}
            </h1>

            {/* Options */}
            <div className="space-y-3">
              {currentQ.options.map((option, index) => {
                const isSelected = quizAnswers[`q${currentQuestion}`] === index;

                return (
                  <button
                    key={index}
                    onClick={() => setQuizAnswers({ ...quizAnswers, [`q${currentQuestion}`]: index })}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all flex items-center gap-4 ${
                      isSelected
                        ? 'border-primary-500 bg-white shadow-sm'
                        : 'border-neutral-200 hover:border-neutral-300 bg-white'
                    }`}
                  >
                    {/* Letter Label */}
                    <span className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isSelected
                        ? 'border-primary-500 text-primary-600 bg-primary-50'
                        : 'border-neutral-300 text-neutral-500'
                    }`}>
                      {optionLetters[index]}
                    </span>

                    {/* Option Text */}
                    <span className="flex-1 font-medium text-neutral-900">{option}</span>

                    {/* Checkmark */}
                    {isSelected && (
                      <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Navigation Buttons */}
            <div className="mt-8 flex items-center gap-3">
              {currentQuestion > 1 && (
                <button
                  onClick={handlePrevQuestion}
                  className="px-6 py-3 border-2 border-neutral-300 text-neutral-700 font-semibold rounded-xl hover:border-neutral-400 transition-colors"
                >
                  ← Previous
                </button>
              )}
              {currentQuestion < totalQuestions ? (
                <button
                  onClick={handleNextQuestion}
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors"
                >
                  Next Question →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={answeredCount < totalQuestions}
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
                >
                  Submit Quiz ({answeredCount}/{totalQuestions} answered)
                </button>
              )}
            </div>
          </div>

          {/* Right Side - Timer & Questions List (Desktop only) */}
          <div className="hidden lg:block w-72 flex-shrink-0 order-3 lg:order-2">
            <div className="sticky top-8">
              {/* Circular Timer */}
              {timerEnabled && (
              <div className="flex flex-col items-center mb-6">
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 mt-2">Time Remaining</span>
              </div>
              )}

              {/* Questions List */}
              <div className="bg-neutral-50 rounded-xl p-4">
                <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Quiz Questions
                </h3>
                <div className="space-y-2">
                  {quizQuestions.map((q, index) => {
                    const questionNum = index + 1;
                    const isAnswered = quizAnswers[`q${questionNum}`] !== undefined;
                    const isCurrent = currentQuestion === questionNum;

                    return (
                      <button
                        key={index}
                        onClick={() => handleQuestionClick(questionNum)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                          isCurrent
                            ? 'bg-primary-100 border border-primary-300'
                            : 'hover:bg-neutral-100'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          isAnswered
                            ? 'bg-green-500 text-white'
                            : isCurrent
                              ? 'bg-primary-500 text-white'
                              : 'bg-neutral-200 text-neutral-600'
                        }`}>
                          {isAnswered ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            questionNum
                          )}
                        </div>
                        <span className={`text-sm font-medium truncate ${
                          isCurrent ? 'text-primary-700' : 'text-neutral-700'
                        }`}>
                          Question {questionNum}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Progress Info */}
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-600">Answered</span>
                    <span className="font-semibold text-primary-600">{answeredCount}/{totalQuestions}</span>
                  </div>
                  <div className="mt-2 w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-all duration-300"
                      style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Quiz Info */}
              <div className="mt-4 bg-neutral-50 rounded-xl p-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Session:</span>
                    <span className="font-medium text-neutral-700">{sessionCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Pass Score:</span>
                    <span className="font-medium text-neutral-700">70%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Points:</span>
                    <span className="font-medium text-neutral-700">{totalQuestions * 10} pts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizActivity;
